import { useState } from 'react'
import { User, Phone, Mail, Briefcase, QrCode } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { ContactQrScannerModal } from '@/components/shared/ContactQrScannerModal'
import { formatPhone } from '@/utils/formatPhone'
import { getQrLeadScanHints, qrLeadScanToastMessage, type QrLeadScanResult } from '@/utils/qrLeadScanFlow'
import { toast } from 'sonner'
import type { CloserLeadFormData } from '@/types'

interface ContatoSectionProps {
  formData: CloserLeadFormData
  onFieldChange: (field: string, value: string) => void
  onMultiFieldChange?: (fields: Partial<CloserLeadFormData>) => void
  errors?: Record<string, string>
  highlightContato?: boolean
  onContatoFilled?: () => void
  onAfterQrScan?: (result: QrLeadScanResult) => void
  /** essential = nome + telefone; optional = cargo + e-mail */
  fields?: 'essential' | 'optional' | 'all'
}

export function ContatoSection({
  formData,
  onFieldChange,
  onMultiFieldChange,
  errors,
  highlightContato = false,
  onContatoFilled,
  onAfterQrScan,
  fields = 'all',
}: ContatoSectionProps) {
  const [scannerOpen, setScannerOpen] = useState(false)

  const applyScan = (result: QrLeadScanResult) => {
    const fields: Partial<CloserLeadFormData> = {}

    if (result.telefone) {
      const digits = result.telefone.replace(/\D/g, '')
      const local = digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits
      fields.telefone = formatPhone(local)
    }

    if (result.nome?.trim()) {
      fields.contato = result.nome.trim()
    }

    if (Object.keys(fields).length === 0) {
      toast.error('Não foi possível extrair dados do QR')
      return
    }

    if (onMultiFieldChange) {
      onMultiFieldChange(fields)
    } else {
      if (fields.contato) onFieldChange('contato', fields.contato)
      if (fields.telefone) onFieldChange('telefone', fields.telefone)
    }

    onAfterQrScan?.(result)

    const hints = getQrLeadScanHints(result)
    toast.info(qrLeadScanToastMessage(hints), { duration: 5000 })
  }

  const showContatoPending = highlightContato && !formData.contato?.trim()
  const highlightNome = showContatoPending

  const handleContatoChange = (value: string) => {
    onFieldChange('contato', value)
    if (value.trim().length >= 2 && highlightContato) {
      onContatoFilled?.()
    }
  }

  const cargoInput = (
    <Input
      label='Cargo'
      value={formData.cargo}
      onChange={e => onFieldChange('cargo', e.target.value)}
      placeholder='Ex: Diretor de Marketing'
      icon={<Briefcase size={14} />}
    />
  )

  const emailInput = (
    <Input
      label='Email'
      type='email'
      value={formData.email}
      onChange={e => onFieldChange('email', e.target.value)}
      placeholder='contato@empresa.com (opcional)'
      icon={<Mail size={14} />}
      error={errors?.email}
    />
  )

  if (fields === 'optional') {
    return (
      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <div className='rounded-lg bg-accent/10 p-1.5 text-accent'>
            <User size={16} />
          </div>
          <span className='text-xs font-bold uppercase tracking-[0.15em] text-text-muted'>
            Contato — complementos
          </span>
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          {cargoInput}
          {emailInput}
        </div>
      </div>
    )
  }

  const showOptional = fields === 'all'

  return (
    <div className='space-y-4'>
      <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-2'>
          <div className='rounded-lg bg-accent/10 p-1.5 text-accent'>
            <User size={16} />
          </div>
          <span className='text-xs font-bold uppercase tracking-[0.15em] text-text-muted'>
            Dados de Contato
          </span>
        </div>
        <Button
          type='button'
          variant='secondary'
          size='sm'
          icon={<QrCode size={14} />}
          onClick={() => setScannerOpen(true)}
        >
          Escanear QR
        </Button>
      </div>

      {showContatoPending && (
        <div className='rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200'>
          <p className='font-semibold'>Falta informar o nome do contato</p>
          <p className='mt-1 text-xs leading-relaxed text-amber-800/90 dark:text-amber-100/90'>
            O QR do WhatsApp traz só o telefone. Digite como a pessoa se apresenta no evento.
          </p>
        </div>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Input
          label='Nome do Contato *'
          value={formData.contato}
          onChange={e => handleContatoChange(e.target.value)}
          placeholder='Nome completo'
          icon={<User size={14} />}
          error={errors?.contato}
          highlighted={highlightNome}
        />
        {showOptional && cargoInput}
        <Input
          label='Telefone *'
          value={formData.telefone}
          onChange={e => onFieldChange('telefone', formatPhone(e.target.value))}
          placeholder='(00) 00000-0000'
          icon={<Phone size={14} />}
          error={errors?.telefone}
        />
        {showOptional && emailInput}
      </div>

      <ContactQrScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={applyScan}
      />
    </div>
  )
}

export default ContatoSection
